import {
    AfterViewChecked,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    QueryList,
    Renderer2, ViewChild,
    ViewChildren,
    ViewEncapsulation
} from '@angular/core';
import {DateTimePeriodService} from "../../services/date-time-period.service";
import {TileDataService} from "../../services/tile_data.service";
import {SeriesDataTableService} from "./series-data-table.service";
import {concatMap, takeUntil, tap} from 'rxjs/operators';
import {of, Subject} from "rxjs";
import {ColumnFormats, ColumnFormatsDict, TableUtilsService} from "../../services/table-utils.service";
import {IDateTimePeriod} from "../../_typing/date-time-period";
import {SeriesDataService} from "../../services/series_data.service";
import {SeriesColumn} from "../../_typing/series-column";
import {HeaderDataService} from "../../services/header_data.service";
import {IDMap, KeyMap, KeyMapMap, ModelID} from "../../_typing/generic-types";
import {SeriesConstantPermissions, SeriesPermissions} from "../../_typing/series-constant-permissions";
import {DateTimeInstanceService} from "../../services/date-time-instance.service";
import {TOOLTIP_SHOW_DELAY} from "../../shared/globals";
import {ComponentEventsTableService} from "../component-events-table/component-events-table.service";
import {SeriesDataTableDownloadService} from "../../services/series-data-table-download.service";
import {TimePeriod} from "../../_typing/components/time-period";
import {FileDownloadOption} from "../../_typing/download-filetype";
import {ITileButton} from "../../_typing/tile-button";
import {debounce as _debounce} from 'lodash-es';
import {ConditionalFormattingService} from "../../services/conditional-formatting.service";
import {Series} from "../../_models/series";
import {
    SeriesDataSeriesGroupDict, SeriesDataTableConfig,
    SeriesDataTableHeaderRows,
    SeriesGroupConfig
} from "../../_typing/config/series-data-table";
import {SeriesDataTableFormService} from "../../forms/series-data-table-form/series-data-table-form.service";
import {RowResizeDirective} from "../../directives/row-resize.directive";
import {ColumnResizeDirective} from "../../directives/column-resize.directive";
import {SeriesEditButtonComponent} from "../../components/series-edit-button/series-edit-button.component";
import {SparklineComponent} from "../sparkline/sparkline.component";
import {SharedModule} from "../../shared/modules/shared-module.module";
import {ChartDialogService} from "../../services/chart-dialog.service";
import {deepCopy} from "../../lib/utils";

@Component({
    selector: 'series-data-table',
    templateUrl: './series-data-table.component.html',
    styleUrls: ['./series-data-table.component.less'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        SharedModule,
        RowResizeDirective,
        ColumnResizeDirective,
        SeriesEditButtonComponent,
        SparklineComponent,
    ],
    providers: [SeriesDataTableService, SeriesDataTableFormService, SeriesDataTableDownloadService]
})
export class SeriesDataTableComponent implements OnInit, OnDestroy, AfterViewChecked {
    private readonly onDestroy = new Subject<void>();
    @Input() config: SeriesDataTableConfig;
    timeDict: KeyMap<TimePeriod> = {};
    series_dict: IDMap<SeriesGroupConfig> = {};
    series_data: any = {};
    series_missing: any = {};
    series_summary: any;
    seriesIds: ModelID[];
    groupDict: SeriesDataSeriesGroupDict = {};
    groupList: string[];

    dtp: IDateTimePeriod;
    format_dict: ColumnFormatsDict = {};
    header_row_count: number;
    show_time_header_dict: SeriesDataTableHeaderRows = {};
    gssColumnDict: KeyMap<SeriesColumn> = {};
    private seriesPermissions: SeriesPermissions;
    private buttons: ITileButton[];
    tileReady: boolean = false;
    showVertical: boolean = true;
    columns: string[];
    timeColumns: string[];
    gssColumns: string[];
    isSticky: { name: boolean, alias: boolean, description: boolean } = {
        'name': true,
        'alias': false,
        'description': false
    };
    conditionalDict: KeyMapMap<Partial<ColumnFormats>> = {};
    formats: KeyMap<ColumnFormatsDict>;
    resizeableColumns: string[] = ['alias', 'name', 'description', 'unit', 'edit', 'time_h', 'time_v'];
    seriesList: Partial<Series>[];

    @ViewChild('horizontalSeriesDataTable', {static: false}) horizontalTable: ElementRef;
    _tableReady: boolean = false;
    @ViewChildren('groupCell', {read: ElementRef}) groupCells: QueryList<ElementRef>;
    tileContent: HTMLElement;
    groupAdjusted: KeyMap<boolean> = {};
    overlayScroll: HTMLElement;
    private scrollListenerAttached = false;
    private _scrollHandler = _debounce((event: Event) => {
        this.adjustVerticalHeights();
    }, 300);
    protected readonly TOOLTIP_SHOW_DELAY = TOOLTIP_SHOW_DELAY;

    ngAfterViewChecked() {
        if (this.horizontalTable && !this._tableReady) {
            this._tableReady = true;
            this.adjustVerticalCells();
        }
    }

    constructor(private datetimePeriodService: DateTimePeriodService,
                private dateInst: DateTimeInstanceService,
                private tileData: TileDataService,
                private tableUtils: TableUtilsService,
                private cps: SeriesDataTableService,
                private chartDialogService:ChartDialogService,
                private seriesDataService: SeriesDataService,
                public componentEventsTableService: ComponentEventsTableService,
                private headerData: HeaderDataService,
                private conditionalService: ConditionalFormattingService,
                private downloadDataService: SeriesDataTableDownloadService,
                private seriesDataTableFormService: SeriesDataTableFormService,
                private renderer: Renderer2) {
    }

    ngOnInit() {
        this.showVertical = this.config.orientation !== 'horizontal';
        this.header_row_count = Object.values(this.config.header_rows).filter(hr => hr === true).length;
        this.showTimeColumn();
        this._getSeriesColumnIdentifier(['name', 'alias', 'description'].filter(c => this.config.header_rows[c]))
        this.seriesDataService.baseColumns.forEach(c => {
            this.gssColumnDict[c.name] = deepCopy(c);
            if(this.config.gss?.column_titles?.[c.name]){
                this.gssColumnDict[c.name].title = this.config.gss.column_titles[c.name];
            }
        });
        this.cps.dataReady.pipe((concatMap((result) => {
            result.series_list.forEach(s => this.series_dict[s.id] = s);
            this.seriesList = result.series_list;
            this.seriesIds = result.series_list.map(s => s.id);
            this.series_data = result.series_data || {};
            this.groupDict = result.group_dict || {};
            this.groupList = Object.keys(this.groupDict);
            this.series_missing = result.series_missing || {};
            this.series_summary = result.series_summary || {};
            this.timeDict = this.cps.getTimes(this.dtp, Object.keys(this.series_data));
            this._setColumns();
            if (Object.keys(this.series_data)?.length) {
                this.initialiseColumnFormats();
            }
            if (!this.seriesIds?.length) return of(null);

            return this.seriesDataService.getSeriesPermissions(this.seriesIds).pipe(tap(permissions => {
                this.seriesPermissions = permissions;
                this.setButtons();
            }));
        }))).subscribe(() => {
            if (!this.showVertical && (this.config.header_rows.process || this.config.header_rows.custom_groups)) {
                this.groupAdjusted = {};
                this.adjustVerticalCells();
            }
            this.tileReady = true;
        });


        this.datetimePeriodService.dtpInitialisedPromise.promise.then(() => {
            this.dtp = this.dateInst.dtp;
            this.cps.getSeriesData(this.config, this.dtp);
        });

        this.dateInst.dateTimePeriodRefreshed$.pipe(takeUntil(this.onDestroy)).subscribe((dtp) => {
            this.dtp = dtp;
            this.cps.getSeriesData(this.config, this.dtp);
        });

        if (this.tileData?.save_content) {
            this.tileData.save_content.pipe(takeUntil(this.onDestroy)).subscribe(event => {
                if (!this.tileData.tile) {
                    return;
                }
                Object.keys(this.format_dict).forEach(seriesId => {
                    if (this.config.column_formats?.[seriesId]?.resize !== false) {
                        if (!this.config.column_formats[seriesId]) {
                            this.config.column_formats[seriesId] = this.tableUtils.getDefaultFormatConfig(this.cps.seriesDataTableMenuOptions);
                        }
                        if (this.format_dict[seriesId]?.width) {
                            this.config.column_formats[seriesId].width = this.format_dict[seriesId].width;
                        }
                        if (this.format_dict[seriesId]?.height) {
                            this.config.column_formats[seriesId].height = this.format_dict[seriesId].height;
                        }
                    }
                })
                this.tileData.tile.attributes.parameters.column_formats = this.config.column_formats;
                this.tableUtils.emitSaveTile(this.tileData);
                this.groupAdjusted = {};
                this.adjustVerticalCells();
            });
        }
    }

    private _setColumns() {
        this.columns = ['edit', 'name', 'description', 'alias', 'unit'];
        this.columns = this.columns.filter(r => this.config.header_rows[r]);
        if (this.config.header_rows.process || this.config.header_rows.custom_groups) {
            this.columns.unshift('group');
        }
        this.gssColumns = this.config.gss?.columns;
        if (this.gssColumns && this.config.show_gss === 'top') {
            this.columns = this.columns.concat(this.gssColumns);
        }
        this.timeColumns = Object.keys(this.series_data);
        this.columns = this.columns.concat(this.timeColumns)
        if (this.gssColumns && this.config.show_gss === 'bottom') {
            this.columns = this.columns.concat(this.gssColumns);
        }
    }

    private _getSeriesColumnIdentifier(identifierColumns: string[]): KeyMap<boolean> {
        let columns = identifierColumns.map(c => c.toLowerCase());
        let isSticky = {'name': true, 'alias': false, 'description': false};
        const isFirst = (columns: string[], firstColumn: string): boolean => {
            return columns.every(c => columns.includes(c)) && columns.every((c) => columns.indexOf(firstColumn) < columns.indexOf(c))
        }
        isSticky.name = columns.includes("name");
        isSticky.alias = (columns.includes("alias") && !columns.includes("name")) || isFirst(["name", "alias"], "alias");
        isSticky.description = columns.includes("description") && !(columns.includes("name") || columns.includes("alias"));
        return isSticky;
    }

    public isEmpty: (obj) => boolean = (obj) => Object.keys(obj).length === 0;

    private initialiseColumnFormats() {
        this.cps.seriesList(this.config)?.forEach(series => {
            this.format_dict[series.id] = this.tableUtils.getColumnFormats(this.config.column_formats?.[series.id], {decimals: null});
        });

        this.resizeableColumns.forEach(col => {
            if (!this.config.column_formats[col]) {
                this.config.column_formats[col] = this.tableUtils.getDefaultFormatConfig(this.cps.seriesDataTableMenuOptions);
            }
            this.format_dict[col] = this.tableUtils.getColumnFormats(this.config.column_formats[col]);
        })

        if (this.config.gss?.column_formats) {
            Object.keys(this.config.gss.column_formats).forEach(column => {
                this.format_dict[column] = this.tableUtils.getColumnFormats(this.config.gss.column_formats?.[column]);
            });
        }
        this.updateConditionalColumnFormats();
    }

    updateConditionalColumnFormats() {
        const rows = Object.keys(this.series_data).map(id => {
            return {id: id};
        });
        let data = {}
        Object.keys(this.series_data).forEach(timestamp => {
            data[timestamp] = {};
            this.seriesList.forEach(series => {
                data[timestamp][series.id] = {value: this.series_data[timestamp][series.attributes.name]}
            })

        })
        let seriesData = {series: {data: data, dict: this.series_dict}};
        this.conditionalDict = this.conditionalService.extractConditions(this.config.conditional_formats || [], rows, this.seriesIds,
            seriesData);
        this.formats = this.tableUtils.combineStyles(this.format_dict, this.conditionalDict, rows);
    }

    private showTimeColumn() {
        this.show_time_header_dict = this.cps.showTimeColumn(this.config);
    }

    public openChartDialog(series_name): void {
        this.chartDialogService.openChartDialog(series_name, this.dateInst.dtp);
    };

    overrideCalculations() {
        this.headerData.getCalculations(this.dateInst.dtp, this.seriesIds, 'hour', 1).then(response => {
            this.cps.getSeriesData(this.config, this.dtp);
        }).catch();


    }

    private allSeriesAllowOverrideCalcs(): boolean {
        return Object.values(this.seriesPermissions).every(permissions => {
                return permissions.includes(SeriesConstantPermissions.override_calculations)
            }
        );
    }

    downloadTableData(fileType: FileDownloadOption = 'formatted_xlsx'): void {
        const tileTitle: string = this.tileData?.tile?.attributes?.title?.trim();
        const downloadTitle: string = tileTitle || 'SeriesDataTable';
        this.downloadDataService.createTableLayoutExcelJS(this.config, this.groupList, this.groupDict, this.series_dict,
            Object.keys(this.timeDict), this.series_data, this.series_summary, this.format_dict, fileType, downloadTitle);
    }

    getEventDataFileType($event) {
        this.componentEventsTableService.openFileFormatModal($event, this.downloadTableData.bind(this));
    }

    getFirstTimeColumn(): string {
        return this.columns.find(col => this.timeColumns.includes(col));
    }

    getFirstHeaderRow(): number {
        return (this.config.header_rows.name || this.config.header_rows.description) ? 0 : 1;
    }

    setButtons() {
        this.buttons = [];

        this.buttons.push({
                name: 'Update Calculations',
                func: () => this.overrideCalculations(),
                params: {},
                class: 'fa fa-calculator hide-xs',
                disabled: !this.allSeriesAllowOverrideCalcs(),
                HoverOverHint: this.allSeriesAllowOverrideCalcs() ? 'Update calculations' : 'You do not have permissions to override calculations on all of these series.'
            },
            {
                name: 'Download Table Data',
                func: ($event) => this.getEventDataFileType($event),
                class: 'small fas fa-file-download hide-xs',
                HoverOverHint: 'Download table data as a .csv or .xlsx file'
            });
        this.tileData.buttonsChanged.next(this.buttons);
    }

    adjustVerticalCells() {
        if (this.showVertical || !(this.config.header_rows.process || this.config.header_rows.custom_groups)) return;
        if (!this.horizontalTable) return;
        this.tileContent = this.horizontalTable.nativeElement.closest('.tile-content');
        setTimeout(() => {
            this.adjustVerticalHeights();
        }, 0);
        if (!this.scrollListenerAttached && this.config.group_always_visible) {
            this.overlayScroll = this.tileContent.querySelector('[data-overlayscrollbars-contents]');
            this.overlayScroll.addEventListener('scroll', this._scrollHandler);
            this.scrollListenerAttached = true;
        }

    }

    adjustVerticalHeights() {
        const tableRect = this.tileContent.getBoundingClientRect();
        this.tableUtils.adjustVerticalHeights(this.groupCells, this.renderer, tableRect, this.groupAdjusted, this.config.group_always_visible);
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.unsubscribe();
        if (this.scrollListenerAttached) {
            this.overlayScroll.removeEventListener('scroll', this._scrollHandler);
            this.scrollListenerAttached = false;
        }
    }
}
